import { useState } from 'react'
import { ClipboardList, Loader2, Search, Settings2, ShoppingCart } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { useReorderList } from '@/hooks/useInventoryMaster'
import {
  REORDER_POINT_SOURCE_LABEL,
  REORDER_STATE_LABEL,
  REORDER_STATE_STYLE,
  formatReorderNumber,
  reorderActionTitle,
} from '@/lib/reorder'
import type { ReorderCounts, ReorderFilter, ReorderRow } from '@/types/inventory'
import { ReorderDialog } from './ReorderDialog'
import { PurchaseOrdersList } from './purchase-orders/PurchaseOrdersList'
import { ReorderCalculator } from './reorder-calculator/ReorderCalculator'
import { PERMISSIONS, usePermission } from '@/lib/permissions'

const ALL_TYPES = 'all'
const MATERIAL_TYPES = ['Consumable', 'Reagent', 'Kit']

/** In display order. `all` has no count of its own. */
const FILTERS: { value: ReorderFilter; label: string; count?: keyof ReorderCounts }[] = [
  { value: 'attention', label: 'Re-order & Out of Stock', count: 'attention' },
  { value: 'reorder', label: 'Low Stock', count: 'reorder' },
  { value: 'out', label: 'Out of Stock', count: 'out' },
  { value: 'expired', label: 'Expired', count: 'expired' },
  { value: 'all', label: 'All' },
]

const EMPTY_TEXT: Record<ReorderFilter, string> = {
  attention: 'Nothing needs re-ordering — every material is above its reorder point.',
  reorder: 'No material is at or below its reorder point.',
  out: 'No material is out of stock.',
  expired: 'No material is holding expired stock.',
  all: 'No materials match.',
}

/**
 * Inventory → Re-Order (z8nrz7f7dz).
 *
 * Every material at or below its reorder point, or out of stock, in one list
 * across all material types — the planner's worklist, where Consumables and
 * Reagents are the catalogue. The decision itself is the server's (see
 * src/lib/reorder.ts for the rule); this tab only lists and filters it.
 */
export function ReorderTab() {
  const [filter, setFilter] = useState<ReorderFilter>('attention')
  const [type, setType] = useState<string>(ALL_TYPES)
  const [search, setSearch] = useState('')
  const [poFor, setPoFor] = useState<string | null>(null)
  const [view, setView] = useState<'order' | 'pos' | 'calc'>('order')
  const { can } = usePermission()
  const canReadPos = can(PERMISSIONS.purchaseOrdersRead)
  const showPos = view === 'pos' && canReadPos
  const showCalc = view === 'calc'
  const views = (
    [
      ['order', 'To order'],
      ['pos', 'Purchase orders'],
      ['calc', 'Calculator'],
    ] as const
  ).filter(([v]) => v !== 'pos' || canReadPos)

  const { data, isLoading, error } = useReorderList(
    filter,
    type === ALL_TYPES ? undefined : type,
    search.trim() || undefined,
  )
  const rows = data?.items ?? []

  return (
    <div className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
      <div className='flex flex-wrap items-center gap-3 border-b border-gray-100 p-4'>
        {/* What to order, what has been ordered, and the what-if calculator.
            A segmented control like the filter beside it, not nested <Tabs>,
            for the same reason. */}
        <div className='flex gap-1 rounded-lg bg-muted p-1' role='group' aria-label='Re-order view'>
            {views.map(([value, label]) => (
              <button
                key={value}
                type='button'
                aria-pressed={view === value}
                onClick={() => setView(value)}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                  view === value ? 'bg-primary text-primary-foreground shadow-sm' : 'text-gray-600 hover:text-gray-900',
                )}
              >
                {label}
              </button>
            ))}
        </div>

        {/* A segmented control, not nested <Tabs>: a second tablist inside
            this tab's panel would confuse the page's tab locators. */}
        {view === 'order' && (
        <div className='flex flex-wrap gap-1 rounded-lg bg-muted p-1' role='group' aria-label='Re-order filter'>
          {FILTERS.map((f) => {
            const n = f.count ? data?.counts[f.count] : undefined
            const active = filter === f.value
            return (
              <button
                key={f.value}
                type='button'
                aria-pressed={active}
                onClick={() => setFilter(f.value)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                )}
              >
                {f.label}
                {n != null && (
                  <span
                    className={cn(
                      'rounded-full px-1.5 text-[11px] tabular-nums',
                      n > 0 && f.value !== 'expired'
                        ? 'bg-red-100 text-red-700'
                        : n > 0
                          ? 'bg-amber-100 text-amber-800'
                          : 'bg-gray-200 text-gray-600',
                    )}
                  >
                    {n}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        )}

        {!showCalc && (
        <div className='relative min-w-[220px] flex-1'>
          <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400' />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={showPos ? 'Search PO number, supplier…' : 'Search material ID, code, name…'}
            className='pl-9'
            name='reorder-search'
            autoComplete='off'
            data-1p-ignore
            data-lpignore='true'
            data-form-type='other'
          />
        </div>
        )}

        {view === 'order' && (
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className='w-[160px] shrink-0'>
              <SelectValue placeholder='All types' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_TYPES}>All types</SelectItem>
              {MATERIAL_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Straight to the PO template editor (z8nrz7f7dz): logo, layout,
            standard templates. Only for those who may change it. */}
        {can(PERMISSIONS.purchaseOrdersManage) && (
          <Link
            to='/configure?tab=purchase-orders'
            className='ml-auto inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50'
            title='Set up the purchase order template: logo, layout and standard templates'
          >
            <Settings2 className='h-4 w-4' />
            PO template
          </Link>
        )}
      </div>

      <div className='p-4'>
        {showCalc ? (
          <ReorderCalculator />
        ) : showPos ? (
          <PurchaseOrdersList search={search} />
        ) : isLoading ? (
          <div className='flex items-center gap-2 p-8 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Loading re-order list…
          </div>
        ) : error ? (
          <p className='py-12 text-center text-sm text-red-600'>
            {error instanceof Error ? error.message : 'Could not load the re-order list.'}
          </p>
        ) : rows.length === 0 ? (
          <div className='py-10 text-center'>
            <ShoppingCart className='mx-auto h-6 w-6 text-muted-foreground' />
            <p className='mt-2 text-sm text-muted-foreground'>{EMPTY_TEXT[filter]}</p>
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <Table className='min-w-[1200px]'>
              <TableHeader>
                <TableRow>
                  {[
                    'Material',
                    'Type',
                    'On hand',
                    'Available',
                    'On order',
                    'Daily consumption',
                    'Total lead time (days)',
                    'Re-order point',
                    'Suggested qty',
                    'Supplier',
                    'State',
                    'PO',
                  ].map((h) => (
                    <TableHead key={h} className='whitespace-nowrap'>
                      {h}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, i) => (
                  <ReorderTableRow
                    key={row.id}
                    row={row}
                    striped={i % 2 === 1}
                    onPo={() => setPoFor(row.id)}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <ReorderDialog materialId={poFor} onClose={() => setPoFor(null)} />
    </div>
  )
}

function ReorderTableRow({ row, striped, onPo }: { row: ReorderRow; striped: boolean; onPo: () => void }) {
  const alert = row.state !== 'ok'
  return (
    <TableRow className={cn(striped && 'bg-muted/10')}>
      <TableCell>
        <div className='font-semibold text-gray-900'>{row.materialId}</div>
        <div className='text-xs text-muted-foreground'>{row.materialName}</div>
      </TableCell>
      <TableCell className='text-gray-600'>{row.materialType}</TableCell>
      <TableCell className='tabular-nums'>{formatReorderNumber(row.onHand)}</TableCell>
      <TableCell className='tabular-nums'>{formatReorderNumber(row.available)}</TableCell>
      <TableCell className='whitespace-nowrap tabular-nums'>
        {formatReorderNumber(row.onOrder)}
        {!!row.openPoCount && (
          <span className='ml-1 text-[11px] text-muted-foreground'>
            {row.openPoCount} open PO{row.openPoCount === 1 ? '' : 's'}
          </span>
        )}
      </TableCell>
      <TableCell className='tabular-nums'>
        {formatReorderNumber(row.dailyConsumption)}
        {row.dailyConsumptionSource === 'history' && (
          <span
            className='ml-1 text-[11px] text-muted-foreground'
            title='Not set on the material: averaged from the last 90 days of consumption.'
          >
            history
          </span>
        )}
      </TableCell>
      <TableCell className='tabular-nums'>{formatReorderNumber(row.totalLeadTimeDays)}</TableCell>
      <TableCell className='whitespace-nowrap tabular-nums'>
        {formatReorderNumber(row.reorderPoint)}
        {row.reorderPointSource && (
          <span className='ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600'>
            {REORDER_POINT_SOURCE_LABEL[row.reorderPointSource]}
          </span>
        )}
      </TableCell>
      <TableCell className='font-semibold tabular-nums'>{formatReorderNumber(row.suggestedQty)}</TableCell>
      <TableCell className='text-gray-600'>{row.supplier ?? '—'}</TableCell>
      <TableCell>
        <div className='flex flex-wrap gap-1'>
          <span
            className={cn(
              'whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
              REORDER_STATE_STYLE[row.state],
            )}
          >
            {REORDER_STATE_LABEL[row.state]}
          </span>
          {row.expired && (
            <span
              className='whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-red-200'
              title={`${formatReorderNumber(row.expiredQty)} units past expiry`}
            >
              Expired
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <button
          type='button'
          title={reorderActionTitle(row.state, row.suggestedQty)}
          aria-label={`Purchase order for ${row.materialId}`}
          onClick={onPo}
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-md border',
            alert
              ? 'border-red-300 bg-red-50 text-red-600 hover:border-red-400 hover:bg-red-100'
              : 'border-gray-200 text-gray-600 hover:border-primary hover:text-primary',
          )}
        >
          <ClipboardList className='h-4 w-4' />
        </button>
      </TableCell>
    </TableRow>
  )
}
